import type { AutonomicsResourceProfile } from 'utils/interfaces'

/** Wire shape for Scenario.settings / Odyssey.settings / ROS Odyssey.settings */
export type OdysseyStartSettings = {
  internet: boolean
  charger: boolean
  battery: number
  clean_water_level: number
  dirty_water_level: number
}

export const DEFAULT_BATTERY_MIN_PCT = 50
export const DEFAULT_CLEAN_WATER_MIN_PCT = 50
export const DEFAULT_DIRTY_WATER_MAX_PCT = 50

const clampPct = (raw: unknown, fallback: number): number => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.min(100, Math.max(0, raw))
  }
  return fallback
}

export const resolveOdysseySettingsDefaults = (
  profile?: AutonomicsResourceProfile | null
): OdysseyStartSettings => {
  const th = profile?.thresholds
  return {
    internet: true,
    charger: true,
    battery: clampPct(th?.battery_min_remaining_pct, DEFAULT_BATTERY_MIN_PCT),
    clean_water_level: clampPct(th?.clean_water_min_remaining_pct, DEFAULT_CLEAN_WATER_MIN_PCT),
    dirty_water_level: clampPct(th?.dirty_water_max_remaining_pct, DEFAULT_DIRTY_WATER_MAX_PCT),
  }
}

export const mergeOdysseySettings = (
  overlay?: Partial<OdysseyStartSettings> | null,
  profile?: AutonomicsResourceProfile | null
): OdysseyStartSettings => {
  const base = resolveOdysseySettingsDefaults(profile)
  if (!overlay) return base
  return {
    internet: typeof overlay.internet === 'boolean' ? overlay.internet : base.internet,
    charger: typeof overlay.charger === 'boolean' ? overlay.charger : base.charger,
    battery: overlay.battery != null ? clampPct(overlay.battery, base.battery) : base.battery,
    clean_water_level:
      overlay.clean_water_level != null
        ? clampPct(overlay.clean_water_level, base.clean_water_level)
        : base.clean_water_level,
    dirty_water_level:
      overlay.dirty_water_level != null
        ? clampPct(overlay.dirty_water_level, base.dirty_water_level)
        : base.dirty_water_level,
  }
}

export const recommendedPctFromProfile = (
  profile?: AutonomicsResourceProfile | null
): {
  battery?: number
  clean_water_level?: number
  dirty_water_level?: number
} => {
  const th = profile?.thresholds
  const out: {
    battery?: number
    clean_water_level?: number
    dirty_water_level?: number
  } = {}
  if (typeof th?.battery_min_remaining_pct === 'number' && Number.isFinite(th.battery_min_remaining_pct)) {
    out.battery = Math.round(th.battery_min_remaining_pct)
  }
  if (
    typeof th?.clean_water_min_remaining_pct === 'number' &&
    Number.isFinite(th.clean_water_min_remaining_pct)
  ) {
    out.clean_water_level = Math.round(th.clean_water_min_remaining_pct)
  }
  if (
    typeof th?.dirty_water_max_remaining_pct === 'number' &&
    Number.isFinite(th.dirty_water_max_remaining_pct)
  ) {
    out.dirty_water_level = Math.round(th.dirty_water_max_remaining_pct)
  }
  return out
}
